import Radio from "@mui/material/Radio";
import MuiRadioGroup, {
  RadioGroupProps as MuiRadioGroupProps,
  useRadioGroup,
} from "@mui/material/RadioGroup";
import {
  ChangeEvent,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactElement,
  ReactNode,
  RefAttributes,
  createContext,
  forwardRef,
  useContext,
  useMemo,
} from "react";
import {
  FormControlLabel,
  FormControlLabelProps,
  FormControlState,
  useFormControl,
} from "../FormControl/FormControl";

type SrRadioGroupContextValue = {
  labelPlacement?: FormControlLabelProps["labelPlacement"];
  readOnly?: boolean;
  size?: FormControlState["size"];
  testIdPrefix?: string;
};

const SrRadioGroupContext = createContext<SrRadioGroupContextValue | undefined>(undefined);

type SharedOptionProps = {
  value: string;
  disabled?: boolean;
  readOnly?: boolean;
  size?: FormControlState["size"];
  labelPlacement?: FormControlLabelProps["labelPlacement"];
  "data-testid"?: string;
};

export interface SrRadioGroupOptionProps extends SharedOptionProps {
  children: ReactNode;
}

export interface SrRadioGroupChoiceCardProps extends SharedOptionProps {
  children: ReactNode;
}

export interface SrRadioGroupProps extends Omit<
  MuiRadioGroupProps,
  "children" | "defaultValue" | "name" | "onChange" | "ref" | "value"
> {
  children: ReactNode;
  name: string;
  value: string;
  size?: FormControlState["size"];
  readOnly?: boolean;
  labelPlacement?: FormControlLabelProps["labelPlacement"];
  onChange: (value: string, e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
}

type ResolvedOptionProps = {
  isChecked: boolean;
  labelPlacement?: FormControlLabelProps["labelPlacement"];
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  size?: FormControlState["size"];
  testId?: string;
};

const useResolvedOptionProps = ({
  value,
  size,
  readOnly,
  labelPlacement,
  "data-testid": dataTestId,
}: SharedOptionProps): ResolvedOptionProps => {
  const group = useRadioGroup();
  const radioGroup = useContext(SrRadioGroupContext);
  const formControl = useFormControl();

  const resolvedSize = size ?? radioGroup?.size ?? formControl?.size ?? "medium";
  const resolvedReadOnly = readOnly ?? radioGroup?.readOnly ?? formControl?.readOnly;
  const resolvedLabelPlacement = labelPlacement ?? radioGroup?.labelPlacement;

  return {
    isChecked: group?.value === value,
    labelPlacement: resolvedLabelPlacement,
    onChange: !resolvedReadOnly && group ? (event) => group.onChange(event, value) : undefined,
    readOnly: resolvedReadOnly,
    size: resolvedSize,
    testId:
      dataTestId ??
      (radioGroup?.testIdPrefix ? `${radioGroup.testIdPrefix}-option-${value}` : undefined),
  };
};

const SrRadioGroupOption = ({
  children,
  value,
  disabled,
  ...props
}: SrRadioGroupOptionProps): ReactElement => {
  const { isChecked, labelPlacement, onChange, readOnly, size, testId } = useResolvedOptionProps({
    value,
    disabled,
    ...props,
  });

  return (
    <FormControlLabel
      value={value}
      label={children}
      labelPlacement={labelPlacement}
      size={size}
      disabled={disabled}
      data-testid={testId}
      sx={readOnly ? { cursor: "default" } : undefined}
      control={
        <Radio
          checked={isChecked}
          disableRipple={readOnly}
          disabled={disabled}
          onChange={onChange}
          readOnly={readOnly}
          size={size}
        />
      }
    />
  );
};

const SrRadioGroupChoiceCard = ({
  children,
  value,
  disabled,
  ...props
}: SrRadioGroupChoiceCardProps): ReactElement => {
  const { isChecked, labelPlacement, onChange, readOnly, size, testId } = useResolvedOptionProps({
    value,
    disabled,
    ...props,
  });

  return (
    <FormControlLabel
      value={value}
      label={children}
      labelPlacement={labelPlacement}
      size={size}
      disabled={disabled}
      disableTypography
      data-testid={testId}
      sx={[
        {
          alignItems: "flex-start",
          border: "1px solid",
          borderColor: isChecked ? "primary.main" : "rgba(17, 17, 17, 0.2)",
          borderRadius: "4px",
          px: "15px",
          py: "10px",
          mb: "10px",
          mx: 0,
          backgroundColor: isChecked ? "primary.container" : "transparent",
          boxShadow: (theme) =>
            isChecked ? `0 0 0 1px ${theme.palette.primary.main} inset` : "none",
          transition: "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
          "& .MuiFormControlLabel-label": {
            width: "100%",
          },
          "&:hover": {
            backgroundColor: isChecked ? "primary.container" : "action.hover",
          },
        },
        ...(readOnly ? [{ cursor: "default" as const }] : []),
      ]}
      control={
        <Radio
          checked={isChecked}
          disableRipple={readOnly}
          disabled={disabled}
          onChange={onChange}
          readOnly={readOnly}
          size={size}
        />
      }
    />
  );
};

const SrRadioGroupBase = forwardRef<HTMLDivElement, SrRadioGroupProps>(
  (
    { children, name, value, size, readOnly, row, onBlur, onChange, labelPlacement, ...props },
    ref,
  ): ReactElement => {
    const formControl = useFormControl();
    const contextValue = useMemo(
      () => ({
        labelPlacement,
        readOnly: readOnly ?? formControl?.readOnly,
        size: size ?? formControl?.size,
        testIdPrefix: name,
      }),
      [formControl?.readOnly, formControl?.size, labelPlacement, name, readOnly, size],
    );

    return (
      <SrRadioGroupContext.Provider value={contextValue}>
        <MuiRadioGroup
          {...props}
          ref={ref}
          name={name}
          row={row}
          value={value || null}
          onBlur={!contextValue.readOnly ? onBlur : undefined}
          onChange={!contextValue.readOnly ? (e, v) => onChange(v, e) : undefined}
        >
          {children}
        </MuiRadioGroup>
      </SrRadioGroupContext.Provider>
    );
  },
);

SrRadioGroupBase.displayName = "SrRadioGroup";
SrRadioGroupOption.displayName = "SrRadioGroup.Option";
SrRadioGroupChoiceCard.displayName = "SrRadioGroup.ChoiceCard";

type SrRadioGroupComponent = ForwardRefExoticComponent<
  PropsWithoutRef<SrRadioGroupProps> & RefAttributes<HTMLDivElement>
> & {
  Option: typeof SrRadioGroupOption;
  ChoiceCard: typeof SrRadioGroupChoiceCard;
};

export const SrRadioGroup = Object.assign(SrRadioGroupBase, {
  ChoiceCard: SrRadioGroupChoiceCard,
  Option: SrRadioGroupOption,
}) as SrRadioGroupComponent;
